import React, { useMemo } from "react";

export interface OptimizedImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "srcSet" | "sizes"> {
  src: string;
  alt: string;
  width: number;
  height: number;
  /** If true, marks this as an LCP image: eager loading + fetchpriority="high" */
  priority?: boolean;
  /** Explicit srcSet. For Unsplash URLs this is auto-generated if omitted. */
  srcSet?: string;
  /** Explicit sizes. Default is auto-detected from common layout patterns. */
  sizes?: string;
}

const UNSPLASH_RE =
  /^https:\/\/images\.unsplash\.com\/photo-\w+/i;

/** Generate responsive srcSet variants for Unsplash URLs */
function buildUnsplashSrcSet(baseUrl: string, widths: number[] = [600, 800, 1200]) {
  try {
    const url = new URL(baseUrl);
    return widths
      .map((w) => {
        url.searchParams.set("w", String(w));
        const q = w >= 1200 ? "65" : "60";
        url.searchParams.set("q", q);
        return `${url.toString()} ${w}w`;
      })
      .join(", ");
  } catch {
    return undefined;
  }
}

/** Detect a sensible default `sizes` based on common responsive breakpoints */
function defaultSizes(layout?: "hero" | "card" | "grid"): string {
  switch (layout) {
    case "hero":
      return "(max-width: 1024px) 100vw, 50vw";
    case "card":
      return "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw";
    case "grid":
      return "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw";
    default:
      return "(max-width: 768px) 100vw, 50vw";
  }
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  priority = false,
  srcSet: explicitSrcSet,
  sizes: explicitSizes,
  loading,
  fetchPriority,
  decoding = "async",
  className = "",
  ...rest
}) => {
  const isUnsplash = useMemo(
    () => UNSPLASH_RE.test(src),
    [src]
  );

  const computedSrcSet = useMemo(() => {
    if (explicitSrcSet) return explicitSrcSet;
    if (isUnsplash) return buildUnsplashSrcSet(src);
    return undefined;
  }, [explicitSrcSet, isUnsplash, src]);

  const computedSizes = useMemo(() => {
    if (explicitSizes) return explicitSizes;
    if (computedSrcSet) return defaultSizes();
    return undefined;
  }, [explicitSizes, computedSrcSet]);

  const computedLoading = priority ? "eager" : (loading ?? "lazy");
  const computedFetchPriority = priority ? "high" : (fetchPriority ?? "auto");

  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      srcSet={computedSrcSet}
      sizes={computedSizes}
      loading={computedLoading}
      fetchPriority={computedFetchPriority}
      decoding={decoding}
      className={className}
      {...rest}
    />
  );
};

export default OptimizedImage;
